import React, { useState, useEffect } from 'react';
import { getPricingAndAvailability, updateAppPricing, updateAppAvailability, getTerritories } from '../../services/api';

function PricingAvailability({ appId, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pricing, setPricing] = useState({
    basePriceTier: 0, // 0 = Free, 1+ = Paid tiers
    discountable: true,
    availableInAllTerritories: true,
    availableInTerritories: [],
    unavailableInTerritories: [],
    preOrderEnabled: false,
    preOrderDate: null,
    b2bLicensingEnabled: false
  });
  const [territories, setTerritories] = useState([]);
  const [priceTiers, setPriceTiers] = useState([]);

  useEffect(() => {
    if (appId) {
      loadPricingData();
      loadTerritories();
    }
  }, [appId]);

  const loadPricingData = async () => {
    setLoading(true);
    try {
      const response = await getPricingAndAvailability(appId);
      console.log('Pricing response:', response.data);
      if (response.data.success && response.data.pricing) {
        setPricing(prevPricing => ({
          ...prevPricing,
          ...response.data.pricing
        }));
      }
    } catch (err) {
      console.error('Failed to load pricing data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTerritories = async () => {
    try {
      const response = await getTerritories();
      if (response.data.success && response.data.territories) {
        setTerritories(response.data.territories);
      }
    } catch (err) {
      console.error('Failed to load territories:', err);
    }
  };

  const handleSave = async () => {
    if (!appId) {
      alert('App ID not available');
      return;
    }

    setSaving(true);
    try {
      const results = [];
      const messages = [];

      // Update pricing if basePriceTier is provided
      if (pricing.basePriceTier !== undefined) {
        const pricingResponse = await updateAppPricing(appId, { basePriceTier: pricing.basePriceTier });
        results.push({ type: 'pricing', response: pricingResponse.data });
        
        // Collect any info messages
        if (pricingResponse.data.message) {
          messages.push(pricingResponse.data.message);
        }
      }

      // Note: Availability updates are limited by Apple's API
      // The toggle state is stored locally but Apple requires using App Store Connect
      // for bulk territory changes
      
      console.log('Update results:', results);

      // Check pricing result
      const pricingResult = results.find(r => r.type === 'pricing');
      if (pricingResult?.response.success) {
        const successMessage = messages.length > 0 
          ? messages.join('\n')
          : 'Settings saved successfully!';
        alert(successMessage);
        if (onUpdate) {
          onUpdate();
        }
      } else if (pricingResult && !pricingResult.response.success) {
        const errorMsg = pricingResult.response.error || 'Failed to update pricing';
        alert('Error: ' + errorMsg);
      } else {
        alert('Settings noted. Some changes may require App Store Connect.');
      }
    } catch (err) {
      console.error('Failed to update pricing/availability:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Unknown error occurred';
      alert('Failed to update: ' + errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleTerritoryToggle = (territoryCode, isAvailable) => {
    if (isAvailable) {
      // Add to available territories and remove from unavailable
      setPricing(prev => ({
        ...prev,
        availableInTerritories: [...prev.availableInTerritories.filter(t => t !== territoryCode), territoryCode],
        unavailableInTerritories: prev.unavailableInTerritories.filter(t => t !== territoryCode)
      }));
    } else {
      // Add to unavailable territories and remove from available
      setPricing(prev => ({
        ...prev,
        unavailableInTerritories: [...prev.unavailableInTerritories.filter(t => t !== territoryCode), territoryCode],
        availableInTerritories: prev.availableInTerritories.filter(t => t !== territoryCode)
      }));
    }
  };

  if (loading) {
    return (
      <div className="loading-inline">
        <div className="spinner"></div>
        <p>Loading pricing and availability...</p>
      </div>
    );
  }

  const isFreeTier = pricing.basePriceTier === 0;

  const commonPriceTiers = [
    { tier: 0, price: 'Free', description: 'Your app will be available for free' },
    { tier: 1, price: '$0.99', description: 'USD $0.99 (or equivalent in local currency)' },
    { tier: 2, price: '$1.99', description: 'USD $1.99 (or equivalent in local currency)' },
    { tier: 3, price: '$2.99', description: 'USD $2.99 (or equivalent in local currency)' },
    { tier: 4, price: '$3.99', description: 'USD $3.99 (or equivalent in local currency)' },
    { tier: 5, price: '$4.99', description: 'USD $4.99 (or equivalent in local currency)' },
    { tier: 10, price: '$9.99', description: 'USD $9.99 (or equivalent in local currency)' },
    { tier: 15, price: '$14.99', description: 'USD $14.99 (or equivalent in local currency)' },
    { tier: 20, price: '$19.99', description: 'USD $19.99 (or equivalent in local currency)' },
    { tier: 30, price: '$29.99', description: 'USD $29.99 (or equivalent in local currency)' },
    { tier: 40, price: '$39.99', description: 'USD $39.99 (or equivalent in local currency)' },
    { tier: 50, price: '$49.99', description: 'USD $49.99 (or equivalent in local currency)' }
  ];

  return (
    <div>
      <h3 style={{ margin: '0 0 1.5rem 0' }}>Pricing and Availability</h3>

      <div style={{ marginBottom: '2rem' }}>
        <p className="text-secondary" style={{ marginBottom: '1rem' }}>
          Set your app's price and choose which territories it will be available in.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        {/* Pricing Section */}
        <div className="pricing-section">
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '18px', fontWeight: '600' }}>
            Pricing
          </h3>
          
          <div className="form-group">
            <label className="form-label">Price Tier</label>
            <select
              className="form-select"
              value={pricing.basePriceTier}
              onChange={(e) => setPricing({ ...pricing, basePriceTier: parseInt(e.target.value) })}
              style={{ maxWidth: '300px' }}
            >
              {commonPriceTiers.map(tier => (
                <option key={tier.tier} value={tier.tier}>
                  {tier.tier === 0 ? 'Free' : `Tier ${tier.tier} - ${tier.price}`}
                </option>
              ))}
            </select>
            <small className="text-secondary" style={{ display: 'block', marginTop: '0.5rem' }}>
              {commonPriceTiers.find(t => t.tier === pricing.basePriceTier)?.description}
            </small>
          </div>

          {!isFreeTier && (
            <div className="form-group">
              <div className="toggle-container">
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={pricing.discountable}
                    onChange={(e) => setPricing({ ...pricing, discountable: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <div style={{ marginLeft: '12px' }}>
                  <span className="form-label" style={{ marginBottom: '0.25rem', display: 'block' }}>
                    Allow discounts
                  </span>
                  <small className="text-secondary" style={{ fontSize: '13px' }}>
                    Allow your app to be included in App Store promotional pricing
                  </small>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Availability Section */}
        <div className="availability-section">
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '18px', fontWeight: '600' }}>
            Availability
          </h3>

          <div className="form-group">
            <div className="toggle-container">
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={pricing.availableInAllTerritories}
                  onChange={(e) => setPricing({ ...pricing, availableInAllTerritories: e.target.checked })}
                />
                <span className="toggle-slider"></span>
              </label>
              <div style={{ marginLeft: '12px' }}>
                <span className="form-label" style={{ marginBottom: '0.25rem', display: 'block' }}>
                  Available in all territories
                </span>
                  <small className="text-secondary" style={{ fontSize: '13px' }}>
                  Make your app available in all current and future App Store territories
                </small>
              </div>
            </div>
          </div>

          {!pricing.availableInAllTerritories && territories.length > 0 && (
            <div className="form-group">
              <label className="form-label">Select Territories</label>
              <div style={{ 
                maxHeight: '300px', 
                overflowY: 'auto', 
                border: '1px solid var(--border-light)', 
                borderRadius: '8px',
                padding: '12px'
              }}>
                {territories.slice(0, 20).map(territory => { // Show first 20 territories
                  const isAvailable = !pricing.unavailableInTerritories.includes(territory.code);
                  return (
                    <div key={territory.code} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      padding: '8px 0',
                      borderBottom: '1px solid var(--border-light)'
                    }}>
                      <span>{territory.name}</span>
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={isAvailable}
                          onChange={(e) => handleTerritoryToggle(territory.code, e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  );
                })}
              </div>
              <small className="text-secondary" style={{ display: 'block', marginTop: '0.5rem' }}>
                Showing first 20 territories. More territories can be managed through App Store Connect.
              </small>
            </div>
          )}
        </div>

        {/* Pre-order Section */}
        {!isFreeTier && (
          <div className="preorder-section">
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '18px', fontWeight: '600' }}>
              Pre-order
            </h3>

            <div className="form-group">
              <div className="toggle-container">
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={pricing.preOrderEnabled}
                    onChange={(e) => setPricing({ ...pricing, preOrderEnabled: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <div style={{ marginLeft: '12px' }}>
                  <span className="form-label" style={{ marginBottom: '0.25rem', display: 'block' }}>
                    Enable pre-order
                  </span>
                  <small className="text-secondary" style={{ fontSize: '13px' }}>
                    Allow users to pre-order your app before release
                  </small>
                </div>
              </div>
            </div>

            {pricing.preOrderEnabled && (
              <div className="form-group">
                <label className="form-label">Expected Release Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={pricing.preOrderDate || ''}
                  onChange={(e) => setPricing({ ...pricing, preOrderDate: e.target.value })}
                  style={{ maxWidth: '200px' }}
                />
                <small className="text-secondary" style={{ display: 'block', marginTop: '0.5rem' }}>
                  The date when your app will be automatically released
                </small>
              </div>
            )}
          </div>
        )}

        {/* Business Section */}
        <div className="business-section">
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '18px', fontWeight: '600' }}>
            Business Features
          </h3>

          <div className="form-group">
            <div className="toggle-container">
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={pricing.b2bLicensingEnabled}
                  onChange={(e) => setPricing({ ...pricing, b2bLicensingEnabled: e.target.checked })}
                />
                <span className="toggle-slider"></span>
              </label>
              <div style={{ marginLeft: '12px' }}>
                <span className="form-label" style={{ marginBottom: '0.25rem', display: 'block' }}>
                  Volume Purchase Program
                </span>
                  <small className="text-secondary" style={{ fontSize: '13px' }}>
                  Make your app available for purchase in volume by businesses and educational institutions
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="alert alert-info" style={{ marginTop: '2rem' }}>
        <strong>Note:</strong> Changes to pricing and availability may take up to 24 hours to take effect 
        across all App Store territories. Pricing changes will be reflected in your next app update or immediately 
        if no update is pending.
      </div>

      <button
        className="btn btn-primary"
        onClick={handleSave}
        disabled={saving}
        style={{ marginTop: '1.5rem' }}
      >
        {saving ? 'Saving...' : 'Update Pricing and Availability'}
      </button>
    </div>
  );
}

export default PricingAvailability;